import type { SafetyPortfolioSiteCoverage } from '../../services/reportsApi';

export interface SiteOwnershipMetadata {
  regionLabel: string | null;
  siteOwner: string | null;
}

function normalize(value?: string | null): string {
  return String(value || '').trim();
}

function firstNonEmpty(...values: Array<string | null | undefined>): string | null {
  for (const value of values) {
    const text = normalize(value);
    if (text) return text;
  }
  return null;
}

export function formatOwnershipLabel(value?: string | null, fallback = 'Unknown'): string {
  return normalize(value) || fallback;
}

export function formatAttentionLocation(siteLabel: string, regionLabel?: string | null): string {
  const site = normalize(siteLabel) || 'Authorized site';
  const region = normalize(regionLabel);
  return region ? `${site} · ${region}` : site;
}

export function resolveSiteOwnershipMetadata(source?: {
  region_label?: string | null;
  region?: string | null;
  site_owner?: string | null;
  owner?: string | null;
  config?: Record<string, unknown> | null;
  address?: Record<string, unknown> | null;
} | null): SiteOwnershipMetadata {
  if (!source) {
    return { regionLabel: null, siteOwner: null };
  }
  const config = (source.config && typeof source.config === 'object') ? source.config : {};
  const address = (source.address && typeof source.address === 'object') ? source.address : {};
  const ownership = (config.ownership && typeof config.ownership === 'object')
    ? config.ownership as Record<string, unknown>
    : {};
  return {
    regionLabel: firstNonEmpty(
      source.region_label,
      source.region,
      typeof config.region_label === 'string' ? config.region_label : null,
      typeof config.region === 'string' ? config.region : null,
      typeof config.regional === 'string' ? config.regional : null,
      typeof config.regional_label === 'string' ? config.regional_label : null,
      typeof ownership.region === 'string' ? ownership.region : null,
      typeof ownership.region_label === 'string' ? ownership.region_label : null,
      typeof address.region === 'string' ? address.region : null,
      typeof address.state === 'string' ? address.state : null,
      typeof address.province === 'string' ? address.province : null,
    ),
    siteOwner: firstNonEmpty(
      source.site_owner,
      source.owner,
      typeof config.site_owner === 'string' ? config.site_owner : null,
      typeof config.owner === 'string' ? config.owner : null,
      typeof config.ehs_owner === 'string' ? config.ehs_owner : null,
      typeof config.ownership_owner === 'string' ? config.ownership_owner : null,
      typeof config.owner_contact === 'string' ? config.owner_contact : null,
      typeof ownership.site_owner === 'string' ? ownership.site_owner : null,
      typeof ownership.owner === 'string' ? ownership.owner : null,
      typeof ownership.ehs_owner === 'string' ? ownership.ehs_owner : null,
      typeof address.owner === 'string' ? address.owner : null,
      typeof address.contact === 'string' ? address.contact : null,
      typeof address.contact_name === 'string' ? address.contact_name : null,
    ),
  };
}

export function buildSiteOwnershipIndex(
  siteCoverage?: SafetyPortfolioSiteCoverage[],
): Record<string, SiteOwnershipMetadata> {
  const index: Record<string, SiteOwnershipMetadata> = {};
  for (const site of siteCoverage || []) {
    const siteId = normalize(site.site_id);
    if (!siteId) continue;
    index[siteId] = resolveSiteOwnershipMetadata(site);
  }
  return index;
}

export function portfolioOwnershipMetadataConfigured(siteCoverage?: SafetyPortfolioSiteCoverage[]): boolean {
  return (siteCoverage || []).some((site) => {
    const metadata = resolveSiteOwnershipMetadata(site);
    return Boolean(metadata.regionLabel || metadata.siteOwner);
  });
}
